import { Effect, pipe } from "effect";
import { UserMerklRewardsInfo } from "../merkl-client/types";
import { KvClientError, KvService } from "./types";
import {
  getMerklHistoricalRewardTokensKey,
  getUserMerklRewardsInfoKey,
} from "./keys";
import { Schema } from "@effect/schema";
import { TTL } from "./constants";
import { Address, ProtocolKey, TokenUidFromString } from "@silo/types";
import { ParseError } from "@effect/schema/ParseResult";
import { RewardToken } from "../types";
import { isNullable } from "effect/Predicate";

export function setMerklUserRewardsInfo(
  userRewardsInfo: UserMerklRewardsInfo
): Effect.Effect<void, ParseError | KvClientError, KvService> {
  return Effect.gen(function* () {
    const { protocolKey, user } = userRewardsInfo;

    const kv = yield* KvService;
    const key = getUserMerklRewardsInfoKey(protocolKey, user);

    const encoded = yield* pipe(
      userRewardsInfo,
      Schema.encode(UserMerklRewardsInfo)
    );

    yield* Effect.tryPromise({
      try: async () => {
        return kv.set(key, encoded, { ex: TTL.FIVE_MINUTES });
      },
      catch: (cause) => new KvClientError({ cause }),
    });
  });
}

export function getMerklUserRewardsInfo(
  protocolKey: ProtocolKey,
  user: Address
): Effect.Effect<
  UserMerklRewardsInfo | null,
  ParseError | KvClientError,
  KvService
> {
  return Effect.gen(function* () {
    const kv = yield* KvService;
    const key = getUserMerklRewardsInfoKey(protocolKey, user);

    const result = yield* Effect.tryPromise({
      try: async () => {
        return kv.get(key);
      },
      catch: (cause) => new KvClientError({ cause }),
    });

    const decoded = yield* pipe(
      result,
      Schema.decodeUnknown(Schema.NullOr(UserMerklRewardsInfo))
    );

    return decoded;
  });
}

export function setMerklHistoricalRewardTokenArray(
  rewardTokenArray: RewardToken[]
): Effect.Effect<void, KvClientError, KvService> {
  return Effect.gen(function* () {
    const kv = yield* KvService;
    const key = getMerklHistoricalRewardTokensKey();

    if (rewardTokenArray.length === 0) {
      return;
    }

    yield* Effect.tryPromise({
      try: () => {
        const pipeline = kv.pipeline();

        const hash: Record<string, string> = {};

        for (const rewardToken of rewardTokenArray) {
          const tokenUidString = pipe(
            rewardToken,
            Schema.encodeSync(TokenUidFromString)
          );

          const encoded = pipe(
            rewardToken,
            Schema.encodeSync(RewardToken),
            JSON.stringify
          );

          hash[tokenUidString] = encoded;
        }

        pipeline.hset(key, hash);

        return pipeline.exec();
      },
      catch: (cause) => new KvClientError({ cause }),
    });
  });
}

export function getMerklHistoricalRewardTokenArray(): Effect.Effect<
  RewardToken[],
  ParseError | KvClientError,
  KvService
> {
  return Effect.gen(function* () {
    const kv = yield* KvService;
    const key = getMerklHistoricalRewardTokensKey();

    const result = yield* Effect.tryPromise({
      try: async () => {
        return kv.hgetall(key);
      },
      catch: (cause) => new KvClientError({ cause }),
    });

    if (isNullable(result)) {
      return [];
    }

    const rewardTokenArray = yield* pipe(
      Object.values(result),
      Schema.decodeUnknown(Schema.mutable(Schema.Array(RewardToken)))
    );

    return rewardTokenArray;
  });
}
